import React, { useEffect, useState, useContext } from 'react';

import { useMutation } from '@apollo/client';

import {
  Actionbar,
  ToolButton,
  PopOut,
  ThemeButton
} from '@ibinder/ui-common-core';

import { buttonTypes } from '@ibinder/ui-common-styles';

import { ellipsisMenu, delete as CFDelete, plus } from '@ibinder/common-icons';

import { GET_USERS_LIST } from '../../../../api/mutations';
import { usersApiRestClient } from '../../../../api/rest.configuration';
import { UsersProps, UserType } from './Users.types';
import { UsersView } from './Users.view';
import { ActionbarContent, PopupButton } from './Users.styles';
import { AddUsersView } from './AddUsers.view';
import { CommonContext } from '../../../../context/CommonProvider';
import { Modal } from '../../../../components/Modal';

export const Users: React.FC<UsersProps> = ({
  data: providerData,
  updateData
}) => {
  const [users, setUsers] = useState<UserType[]>();
  const [selectedIds, setSelectedIds] = useState<string[]>([]);

  const [getUsersList] = useMutation(GET_USERS_LIST, {
    client: usersApiRestClient,
    onCompleted: (response) => {
      setUsers(response?.usersList);
    }
  });

  const { openModal, closeModal } = useContext(CommonContext);
  const [addUsersModal, openAddUsersModal] = useState(false);

  let addedUsers: UserType[] = [];
  const onChangeData = (selectedUsers: UserType[]) => {
    addedUsers = selectedUsers;
  };

  const getAddUsersModal = () => {
    return (
      <Modal
        headerText={'Add users'}
        fullHeightModal={true}
        onClose={() => {
          openAddUsersModal(false);
        }}
        open={addUsersModal}
        actionButton={
          <ThemeButton
            onClick={() => {
              onAddUsers();
            }}
            buttonType={buttonTypes.Primary}
          >
            Add
          </ThemeButton>
        }
      >
        <AddUsersView existingUsers={users} updateData={onChangeData} />
      </Modal>
    );
  };

  const onAddUsersAction = () => {
    openAddUsersModal(true);
  };

  const onAddUsers = () => {
    const allUsers = [...users, ...addedUsers];
    setUsers(allUsers);
    openAddUsersModal(false);
    updateData(
      'omsUsers',
      allUsers.map((u) => ({
        userId: u.id
      }))
    );
  };

  const onRemoveUsersAction = (userIds: string[]) => {
    if (userIds.length) {
      openModal({
        headerText: 'Remove user(s)',
        children:
          userIds.length > 1 ? (
            <div>{userIds.length} user(s) will be removed.</div>
          ) : (
            <div>
              The user {users.find((u) => u.id === userIds[0])?.fullName} will
              be removed.
            </div>
          ),
        isError: true,
        actionButton: (
          <ThemeButton
            onClick={() => {
              const newUsers = users.filter((u) =>
                userIds.every((id) => id !== u.id)
              );
              setUsers(newUsers);
              updateData(
                'omsUsers',
                newUsers.map((u) => ({
                  userId: u.id
                }))
              );
              closeModal();
            }}
            buttonType={buttonTypes.Primary}
          >
            Remove
          </ThemeButton>
        )
      });
    }
  };

  const Actions: Record<
    string,
    { label: string; icon: any; onAction: () => void }
  > = {
    addUsers: { label: 'Add users', icon: plus, onAction: onAddUsersAction },
    removeUsers: {
      label: 'Remove',
      icon: CFDelete,
      onAction: () => onRemoveUsersAction(selectedIds)
    }
  };

  const onActionSelect = (action) => {
    switch (action) {
      case Actions.addUsers:
        onAddUsersAction();
        break;
      case Actions.removeUsers:
        onRemoveUsersAction(selectedIds);
        break;
    }
  };

  const getActionBar = () => {
    return (
      <Actionbar makeSticky={false}>
        <ActionbarContent>
          {Object.keys(Actions).map((action) => {
            return (
              <ToolButton onClick={Actions[action].onAction} type="button">
                {Actions[action].icon} <span>{Actions[action].label}</span>
              </ToolButton>
            );
          })}
          <PopOut
            button={(ref, toggle) => (
              <ToolButton type="button" ref={ref} onClick={toggle}>
                {ellipsisMenu}
              </ToolButton>
            )}
            item={(key, action) => (
              <>
                <PopupButton onClick={action.onAction}>
                  {action.icon}
                  <span>{action.label}</span>
                </PopupButton>
              </>
            )}
            onSelect={onActionSelect}
            data={Actions}
            placement={'bottom-end'}
          />
        </ActionbarContent>
      </Actionbar>
    );
  };

  useEffect(() => {
    getUsersList({
      variables: {
        body: providerData?.omsUsers?.map((u) => u.userId)
      }
    });
  }, []);

  return (
    <>
      {getAddUsersModal()}
      {getActionBar()}
      {users && (
        <UsersView
          data={users}
          onRemoveUser={(user) => onRemoveUsersAction([user.id])}
          selectedIds={selectedIds}
          setSelectedIds={setSelectedIds}
        />
      )}
    </>
  );
};
